import React from "react";
import "./styles.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faUser, faFloppyDisk, faXmark } from "@fortawesome/free-solid-svg-icons";
import FloatingActions, { FloatingAction } from "../FloatingActions";
import Card from "../Card";
import UserSelectAdd, { UserOption } from "../UserSelectAdd";
import axios from "axios";

export interface NewEmployeCardProps {
    cardNumber: string;
    onSave: (userId: string | null) => void;
}

const NewEmployeCard: React.FC<NewEmployeCardProps> = ({ cardNumber, onSave }) => {

    const [usersState, setUsersState] = React.useState<UserOption[]>([]);
    const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);

    const [firstName, setFirstName] = React.useState("");
    const [lastName, setLastName] = React.useState("");
    const [personalNumber, setPersonalNumber] = React.useState("");

    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        setError(null);
        loadUsers();
    }, []);

    const validateNewUser = () => {
        const fn = firstName.trim();
        const ln = lastName.trim();
        const pn = personalNumber.trim();

        if (!fn || !ln || !pn) {
            return "Vyplňte jméno, příjmení a osobní číslo.";
        }
        if (pn.length < 3) {
            return "Osobní číslo je příliš krátké.";
        }
        return null;
    };

    const onCreateOrAssign = async () => {
        setError(null);

        if (selectedUserId) {
            await updateItem(selectedUserId, cardNumber);
            onSave(selectedUserId);
            return;
        }

        const validationError = validateNewUser();
        if (validationError) {
            setError(validationError);
            return;
        }
        const newUserId = await insertItem(firstName, lastName, personalNumber, cardNumber);
        onSave(newUserId);
    };

    const actions: FloatingAction[] = [
        {
            key: "cancel",
            label: "Zrušit",
            icon: <FontAwesomeIcon icon={faXmark} />,
            onClick: () => onSave(null),
        },
        {
            key: "save",
            label: "Uložit",
            icon: <FontAwesomeIcon icon={faFloppyDisk} />,
            onClick: onCreateOrAssign,
        },
    ];

    const loadUsers = async () => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/user/getAll",
                })
            );

            setUsersState(res.data.users);

        } catch (err) {
            throw err;
        }
    }

    const insertItem = async (firstName: string, lastName: string, personalNumber: string, cardid: string) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/user/insert",
                data: {
                    name: `${lastName} ${firstName}`,
                    cardid: cardNumber,
                    personalnumber: personalNumber
                },
            });

            return res.data.id;
        } catch (err) {
            console.error("Failed to insert object", err);
            throw err;
        }
    };

    const updateItem = async (userId: string, cardNumber: string) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/user/update",
                data: {
                    id: userId,
                    cardid: cardNumber
                },
            });

            return res.data.id;
        } catch (err) {
            console.error("Failed to update object", err);
            throw err;
        }
    };

    return (
        <Card title="Přidat zaměstnance">
            <div className="nec-stack">

                <UserSelectAdd
                    header="Zaměstnanec"
                    users={usersState}
                    valueId={selectedUserId}
                    onChange={(id) => setSelectedUserId(id)}
                    hideHint={true}
                />

                {selectedUserId == null ? (
                    <>
                        <div className="nec-block">
                            <div className="nec-header">Jméno</div>
                            <div className="nec-control">
                                <input
                                    className="nec-input"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    placeholder="Jméno..."
                                />
                            </div>
                        </div>

                        <div className="nec-block">
                            <div className="nec-header">Příjmení</div>
                            <div className="nec-control">
                                <input
                                    className="nec-input"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    placeholder="Příjmení..."
                                />
                            </div>
                        </div>

                        <div className="nec-block">
                            <div className="nec-header">Osobní číslo</div>
                            <div className="nec-control">
                                <input
                                    className="nec-input"
                                    value={personalNumber}
                                    onChange={(e) => setPersonalNumber(e.target.value)}
                                    placeholder="Osobní číslo..."
                                />
                            </div>
                            <div className="nec-hint">Osobní čísla oddělte čárkami</div>
                        </div>
                    </>
                ) : null}

                <div className="nec-block">
                    <div className="nec-header">Číslo karty</div>
                    <div className="nec-control">
                        <input className="nec-input" value={cardNumber} readOnly />
                    </div>
                    <div className="nec-hint">Načteno ze čtečky</div>
                </div>

                {error ? <div className="form-error">{error}</div> : null}
            </div>

            {actions?.length ? (
                <div className="item-list-actions">
                    <FloatingActions actions={actions} />
                </div>
            ) : null}
        </Card>
    );

};

export default NewEmployeCard;